/**
 * The Compliance Hub's two calendars (Medicare, Medicaid) on PSI.
 *
 * One small card: a mini month per program, Medicare above Medicaid, a dot per
 * day that says what kind of day it is, the items of a picked day under its
 * calendar, and the next few dates coming up. A click on any item opens the
 * Hub's own detail panel, with the Hub's own status, assignment, guidance and email controls
 * against the same endpoints, so working here is working in the Hub.
 *
 * PSI's own counts and lists never include these obligations.
 */
import React, { useEffect, useMemo, useState } from 'react';
import DetailPanel from '../../Calendar/components/DetailPanel';
import { itemKind } from '../../Calendar/components/DualProgramCalendar';

const API = process.env.REACT_APP_API_URL || '';
const DUE_SOON_DAYS = 14;
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const PROGRAMS = [
  { key: 'medicare', title: 'Medicare', sub: 'CMS', color: '#2563EB', soft: 'rgba(37,99,235,0.12)' },
  { key: 'medicaid', title: 'Medicaid', sub: 'state contract', color: '#059669', soft: 'rgba(5,150,105,0.12)' },
];

/* ── The Hub page's own helpers, carried over unchanged so both screens agree ── */
const parseDateOnly = (value) => {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const formatDate = (value) => {
  if (!value) return '—';
  const parsed = parseDateOnly(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(parsed);
};
const formatDateDisplay = (item) => {
  if (item.start_date && item.end_date) return `${formatDate(item.start_date)} – ${formatDate(item.end_date)}`;
  return formatDate(item.due_date);
};
const getEffectiveStatus = (item) => {
  const stored = (item.display_status || '').toLowerCase();
  if (stored === 'completed' || stored === 'satisfied' || stored === 'remediated') return 'completed';
  if (stored === 'no_action_needed' || stored === 'no action needed') return 'no_action_needed';
  if (stored === 'in_progress') return 'in_progress';
  const due = (item.due_date || item.end_date) ? parseDateOnly(item.due_date || item.end_date) : null;
  if (!due || Number.isNaN(due.getTime())) return 'upcoming';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dueOnly = new Date(due); dueOnly.setHours(0, 0, 0, 0);
  if (dueOnly < today) return 'overdue';
  const windowEnd = new Date(today); windowEnd.setDate(windowEnd.getDate() + DUE_SOON_DAYS);
  if (dueOnly <= windowEnd) return 'due_soon';
  return 'upcoming';
};
const statusStyle = (effectiveStatus) => {
  const n = (effectiveStatus || '').toLowerCase();
  if (n === 'overdue') return 'bg-red-100 text-red-700 border border-red-200';
  if (n === 'due_soon') return 'bg-amber-100 text-amber-800 border border-amber-200';
  if (n === 'completed') return 'bg-slate-100 text-slate-600 border border-slate-200';
  if (n === 'no_action_needed') return 'bg-slate-100 text-slate-600 border border-slate-200';
  if (n === 'in_progress') return 'bg-sky-100 text-sky-700 border border-sky-200';
  return 'bg-blue-50 text-blue-700 border border-blue-200';
};
const effectiveStatusLabel = (effectiveStatus) => {
  const n = (effectiveStatus || '').toLowerCase();
  if (n === 'overdue') return 'Overdue';
  if (n === 'due_soon') return 'Due soon';
  if (n === 'completed') return 'Completed';
  if (n === 'no_action_needed') return 'No action needed';
  if (n === 'in_progress') return 'In progress';
  return 'Upcoming';
};
const calendarStatusLabel = (item) => {
  if (item.source !== 'calendar') return effectiveStatusLabel(getEffectiveStatus(item));
  const stored = (item.display_status || '').toLowerCase();
  if (stored === 'remediated') return 'Remediated';
  return effectiveStatusLabel(getEffectiveStatus(item));
};
const isCalendarNonActionable = (item) => item.source === 'calendar' && item.actionable === false;
const isCalendarObligationCompleted = (item) => {
  const s = (item.display_status || '').toLowerCase();
  return s === 'completed' || s === 'remediated' || s === 'satisfied';
};

/* ── Small date helpers ── */
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const itemDate = (item) => parseDateOnly(item.due_date || item.end_date);
const monthLabel = (d) => d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const monthCells = (monthDate) => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first); start.setDate(first.getDate() - first.getDay()); // Sunday first, like the Hub
  const cells = [];
  for (let i = 0; i < 42; i += 1) { const d = new Date(start); d.setDate(start.getDate() + i); cells.push(d); }
  // Drop a trailing week that is entirely next month.
  const lastRow = cells.slice(35);
  return lastRow.every((d) => d.getMonth() !== monthDate.getMonth()) ? cells.slice(0, 35) : cells;
};
const dotColor = (status, program) => {
  if (status === 'overdue') return '#DC2626';
  if (status === 'due_soon') return '#D97706';
  if (status === 'completed' || status === 'no_action_needed') return '#CBD5E1';
  return program.color;
};
const relDue = (item) => {
  const d = itemDate(item); if (!d) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return `Overdue ${-diff}d`;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff <= 14) return `In ${diff}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const relColor = (item) => {
  const s = getEffectiveStatus(item);
  return s === 'overdue' ? 'text-red-600' : s === 'due_soon' ? 'text-amber-600' : 'text-slate-500';
};

export default function HubCalendars({ onOpenApp }) {
  const [obligations, setObligations] = useState([]);
  const [isCompliance, setIsCompliance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignmentFilterDeptId, setAssignmentFilterDeptId] = useState('');
  const [assigningId, setAssigningId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [actionableUpdatingId, setActionableUpdatingId] = useState(null);
  const [guidanceByObligationId, setGuidanceByObligationId] = useState({});

  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [focusDay, setFocusDay] = useState(null); // the day whose items are listed under its calendar


  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const [obligationsRes, visibilityRes] = await Promise.all([
          fetch(`${API}/api/calendar/obligations`, { credentials: 'include' }),
          fetch(`${API}/api/calendar/visibility`, { credentials: 'include' }),
        ]);
        if (!isMounted) return;
        if (!obligationsRes.ok) throw new Error('Failed to load calendar obligations');
        const data = await obligationsRes.json();
        setObligations(Array.isArray(data) ? data : []);
        const vis = visibilityRes.ok ? await visibilityRes.json().catch(() => ({})) : {};
        setIsCompliance(!!vis.isCompliance);
        setError('');
      } catch (e) {
        if (isMounted) { setError(e.message || 'Failed to load calendar obligations'); setObligations([]); }
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!isCompliance) return;
    let isMounted = true;
    fetch(`${API}/api/departments`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => { if (isMounted && Array.isArray(data)) setDepartments(data); })
      .catch(() => {});
    return () => { isMounted = false; };
  }, [isCompliance]);

  useEffect(() => {
    if (!isCompliance) return;
    let isMounted = true;
    const url = assignmentFilterDeptId
      ? `${API}/api/calendar/assignable-users?department_id=${encodeURIComponent(assignmentFilterDeptId)}`
      : `${API}/api/calendar/assignable-users`;
    fetch(url, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { users: [] }))
      .then((data) => { if (isMounted && Array.isArray(data.users)) setAssignableUsers(data.users); })
      .catch(() => {});
    return () => { isMounted = false; };
  }, [isCompliance, assignmentFilterDeptId]);

  const items = useMemo(() => (obligations || []).map((o) => ({ ...o, source: 'calendar' })), [obligations]);
  const liveSelected = useMemo(() => (selectedItem ? items.find((i) => i.id === selectedItem.id) || selectedItem : null), [selectedItem, items]);
  const byProgram = useMemo(() => ({
    medicare: items.filter((i) => itemKind(i) === 'medicare'),
    medicaid: items.filter((i) => itemKind(i) !== 'medicare'),
  }), [items]);
  const cells = useMemo(() => monthCells(monthDate), [monthDate]);
  const todayKey = dayKey(new Date());
  const inMonth = (d) => d.getMonth() === monthDate.getMonth();
  const byDay = (list) => {
    const map = new Map();
    for (const it of list) { const d = itemDate(it); if (!d) continue; const k = dayKey(d); if (!map.has(k)) map.set(k, []); map.get(k).push(it); }
    return map;
  };
  const nextUp = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return items
      .filter((i) => { const s = getEffectiveStatus(i); return s !== 'completed' && s !== 'no_action_needed' && itemDate(i); })
      .sort((a, b) => itemDate(a) - itemDate(b))
      .filter((i) => itemDate(i) >= new Date(today.getTime() - 30 * 86400000))
      .slice(0, 4);
  }, [items]);
  const openCount = (list) => list.filter((i) => { const s = getEffectiveStatus(i); return s !== 'completed' && s !== 'no_action_needed'; }).length;

  const patch = async (obligationId, payload) => {
    const res = await fetch(`${API}/api/calendar/obligations/${obligationId}`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Update failed'); }
    return res.json();
  };
  const handleStatusChange = async (obligationId, newDisplayStatus) => {
    setStatusUpdatingId(obligationId);
    try {
      const updated = await patch(obligationId, { display_status: newDisplayStatus });
      setObligations((prev) => prev.map((o) => (o.id === obligationId ? { ...o, display_status: updated.display_status } : o)));
    } catch (e) { setError(e.message || 'Failed to update status'); }
    finally { setStatusUpdatingId(null); }
  };
  const handleActionableChange = async (obligationId, actionable) => {
    setActionableUpdatingId(obligationId);
    try {
      const updated = await patch(obligationId, { actionable: !!actionable });
      setObligations((prev) => prev.map((o) => (o.id === obligationId ? { ...o, actionable: updated.actionable } : o)));
    } catch (e) { setError(e.message || 'Failed to update actionable'); }
    finally { setActionableUpdatingId(null); }
  };
  const handleAssignmentChange = async (obligationId, payload) => {
    if (!isCompliance) return;
    setAssigningId(obligationId);
    try {
      const updated = await patch(obligationId, payload);
      setObligations((prev) => prev.map((o) => (o.id === obligationId ? {
        ...o,
        assigned_to_user_id: updated.assigned_to_user_id,
        assignee_name: updated.assignee_name,
        assignee_email: updated.assignee_email,
        responsible_department_id: updated.responsible_department_id,
        responsible_department_name: updated.responsible_department_name,
        assignment_mode: updated.assignment_mode,
      } : o)));
    } catch (e) { setError(e.message || 'Failed to update assignment'); }
    finally { setAssigningId(null); }
  };
  const handleFetchGuidance = async (obligationId) => {
    setGuidanceByObligationId((prev) => ({ ...prev, [obligationId]: { ...prev[obligationId], loading: true } }));
    try {
      const res = await fetch(`${API}/api/calendar/obligations/${obligationId}/guidance`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to get guidance');
      setObligations((prev) => prev.map((o) => (o.id !== obligationId ? o : {
        ...o,
        title: data.title !== undefined ? String(data.title) : (o.title ?? ''),
        ai_guidance_status: data.status,
        ai_guidance_actions: data.actions ?? [],
        ai_guidance_by_whom: data.byWhom ?? '',
        ai_guidance_message: data.message ?? '',
        ai_guidance_certain: data.certain === true,
        ai_guidance_interpretation_type: data.interpretationType ?? null,
      })));
    } catch (e) {
      setGuidanceByObligationId((prev) => ({ ...prev, [obligationId]: { ...prev[obligationId], loading: false, error: e.message || 'Failed to get guidance' } }));
      return;
    } finally {
      setGuidanceByObligationId((prev) => ({ ...prev, [obligationId]: { ...prev[obligationId], loading: false } }));
    }
  };
  const renderObligationAccountable = (item, compact = false) => {
    const m0 = (item.assignment_mode || 'single_user').toLowerCase();
    if (!isCompliance) {
      const text = m0 === 'department_all' && item.responsible_department_name
        ? `All in ${item.responsible_department_name}`
        : (item.assignee_name || item.assignee_email || '—');
      return <span className={compact ? 'text-xs text-slate-700' : 'text-sm text-slate-700'}>{text}</span>;
    }
    const baseClass = compact ? 'text-xs' : 'text-sm';
    return (
      <div className="space-y-1.5">
        <select value={m0}
          onChange={(e) => {
            const m = e.target.value;
            if (m === 'department_all') {
              const deptId = item.responsible_department_id || (departments[0]?.id ?? null);
              if (deptId) handleAssignmentChange(item.id, { assignment_mode: 'department_all', responsible_department_id: deptId, assigned_to_user_id: null });
            } else {
              handleAssignmentChange(item.id, { assignment_mode: 'single_user', assigned_to_user_id: item.assigned_to_user_id || null });
            }
          }}
          disabled={assigningId === item.id}
          className={`${baseClass} bg-white border border-slate-200 rounded px-2 py-1 w-full`}>
          <option value="single_user">Single user</option>
          <option value="department_all">All in department</option>
        </select>
        {m0 === 'department_all' ? (
          <select value={item.responsible_department_id || ''}
            onChange={(e) => handleAssignmentChange(item.id, { assignment_mode: 'department_all', responsible_department_id: e.target.value || null })}
            disabled={assigningId === item.id}
            className={`${baseClass} bg-white border border-slate-200 rounded px-2 py-1 w-full`}>
            <option value="">— Select department</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        ) : (
          <>
            <select value={assignmentFilterDeptId} onChange={(e) => setAssignmentFilterDeptId(e.target.value || '')}
              className={`${baseClass} bg-white border border-slate-200 rounded px-2 py-1 w-full`} title="Filter users by department">
              <option value="">All departments</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select value={item.assigned_to_user_id || ''}
              onChange={(e) => handleAssignmentChange(item.id, { assignment_mode: 'single_user', assigned_to_user_id: e.target.value || null })}
              disabled={assigningId === item.id}
              className={`${baseClass} bg-white border border-slate-200 rounded px-2 py-1 w-full`}>
              <option value="">— Unassigned</option>
              {assignableUsers.map((u) => <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>)}
            </select>
          </>
        )}
      </div>
    );
  };
  const onOpenInEmbeddedView = (url) => { setSelectedItem(null); if (url) window.location.href = url; };

  const shiftMonth = (delta) => { setMonthDate((p) => new Date(p.getFullYear(), p.getMonth() + delta, 1)); setFocusDay(null); };
  const pickDay = (d, has) => { if (!has) return; const k = dayKey(d); setFocusDay((f) => (f === k ? null : k)); };
  const isDone = (it) => { const st = getEffectiveStatus(it); return st === 'completed' || st === 'no_action_needed'; };
  // The Asana check: one click completes, one click reopens. Same handler the Hub panel uses.
  const toggleDone = (e, it) => { e.stopPropagation(); if (statusUpdatingId === it.id) return; handleStatusChange(it.id, isCalendarObligationCompleted(it) ? 'upcoming' : 'completed'); };

  const CheckCircle = ({ it }) => {
    const done = isCalendarObligationCompleted(it); const busy = statusUpdatingId === it.id;
    return (
      <span role="button" tabIndex={0} aria-label={done ? 'Reopen' : 'Mark complete'} title={done ? 'Reopen' : 'Mark complete'}
        onClick={(e) => toggleDone(e, it)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleDone(e, it); }}
        className={`flex-shrink-0 w-[18px] h-[18px] rounded-full border-[1.5px] flex items-center justify-center transition-colors ${busy ? 'opacity-50' : ''} ${done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 text-transparent hover:border-emerald-500 hover:text-emerald-500 hover:bg-emerald-50'}`}>
        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
      </span>
    );
  };

  const ItemRow = ({ it, program, showDate }) => {
    const st = getEffectiveStatus(it); const done = isDone(it);
    return (
      <div role="button" tabIndex={0} onClick={() => setSelectedItem(it)} onKeyDown={(e) => { if (e.key === 'Enter') setSelectedItem(it); }}
        className="group w-full grid grid-cols-[18px_minmax(0,1fr)_auto] gap-2.5 items-center px-3 py-[7px] text-left cursor-pointer border-t first:border-t-0 border-slate-100 hover:bg-slate-50 transition-colors">
        <CheckCircle it={it} />
        <span className="min-w-0">
          <span className={`block text-[12.5px] leading-snug truncate ${done ? 'text-slate-400 line-through' : 'text-slate-800 font-medium'}`}>{it.title}</span>
          {showDate && <span className="block text-[11px] text-slate-400 truncate"><i className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: program.color }} />{program.title}{it.assignee_name ? ` · ${it.assignee_name}` : it.responsible_department_name ? ` · ${it.responsible_department_name}` : ''}</span>}
        </span>
        <span className={`text-[11px] font-semibold tabular-nums whitespace-nowrap ${done ? 'text-slate-300' : relColor(it)}`}>{done ? 'Done' : relDue(it)}</span>
      </div>
    );
  };

  /* One program: mini month with up to three dots per day, and the picked day's items under it. */
  const MiniMonth = ({ program, list }) => {
    const map = byDay(list);
    const focusItems = focusDay ? (map.get(focusDay) || []) : [];
    const focusDate = focusItems.length ? itemDate(focusItems[0]) : null;
    return (
      <div className="min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-800">
            <i className="inline-block w-2.5 h-2.5 rounded-[3px]" style={{ background: program.color }} />{program.title}
            <span className="text-[11px] font-normal text-slate-400">{program.sub}</span>
          </span>
          <span className="text-[11px] font-medium text-slate-400 tabular-nums">{openCount(list)} open</span>
        </div>
        <div className="grid grid-cols-7 text-center">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <span key={d} className="text-[9.5px] font-semibold uppercase tracking-[0.04em] text-slate-400 pb-1">{d}</span>)}
          {cells.map((d) => {
            const k = dayKey(d); const on = map.get(k) || []; const isToday = k === todayKey; const isFocus = k === focusDay && on.length > 0;
            const dots = on.slice(0, 3).map((it) => dotColor(getEffectiveStatus(it), program));
            return (
              <button key={k} type="button" onClick={() => pickDay(d, on.length > 0)} title={on.length ? `${on.length} item${on.length === 1 ? '' : 's'}` : undefined}
                className={`group relative h-[30px] flex flex-col items-center justify-start pt-[3px] rounded-lg transition-colors ${on.length ? 'cursor-pointer hover:bg-slate-100' : 'cursor-default'} ${isFocus ? 'bg-slate-100' : ''}`}>
                <span className={`w-5 h-5 leading-5 rounded-full text-[10.5px] tabular-nums ${isToday ? 'bg-slate-900 text-white font-bold' : inMonth(d) ? 'text-slate-700' : 'text-slate-300'}`}>{d.getDate()}</span>
                {on.length > 0 && (
                  <span className="flex items-center gap-[2px] mt-[1px]">
                    {dots.map((c, i) => <i key={i} className="w-[4px] h-[4px] rounded-full" style={{ background: c }} />)}
                    {on.length > 3 && <span className="text-[8px] leading-none text-slate-400">+</span>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {focusItems.length > 0 && (
          <div className="mt-1.5 rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-slate-400 bg-slate-50 border-b border-slate-100">
              {focusDate ? focusDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : 'Selected day'}
            </div>
            {focusItems.map((it) => <ItemRow key={it.id} it={it} program={program} />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <section aria-label="Compliance Hub calendars" className="rounded-2xl border border-slate-200/90 bg-white overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-[14px] font-semibold text-slate-900 truncate">{monthLabel(monthDate)}</h3>
          <button type="button" onClick={() => { const d = new Date(); setMonthDate(new Date(d.getFullYear(), d.getMonth(), 1)); setFocusDay(null); }} className="px-2 h-6 rounded-md border border-slate-200 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">Today</button>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month" className="w-7 h-7 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg></button>
          <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month" className="w-7 h-7 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg></button>
          <button type="button" onClick={() => onOpenApp?.('calendar')} className="ml-1 text-[11px] font-semibold text-[#E5601F] hover:underline whitespace-nowrap">Open Hub</button>
        </div>
      </div>

      {error && <div className="mx-4 mb-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-[12px] px-3 py-2">{error}</div>}

      {loading ? (
        <div className="px-4 pb-4 text-[12px] text-slate-400">Loading calendars…</div>
      ) : (
        <>
          <div className="px-4 pb-3 grid gap-4">
            {PROGRAMS.map((p) => <MiniMonth key={p.key} program={p} list={byProgram[p.key]} />)}
          </div>
          <div className="px-4 pb-2 flex items-center gap-3 text-[10.5px] text-slate-400">
            <span className="inline-flex items-center gap-1"><i className="w-[5px] h-[5px] rounded-full bg-[#DC2626]" />Overdue</span>
            <span className="inline-flex items-center gap-1"><i className="w-[5px] h-[5px] rounded-full bg-[#D97706]" />Due soon</span>
            <span className="inline-flex items-center gap-1"><i className="w-[5px] h-[5px] rounded-full bg-slate-300" />Done</span>
          </div>
          {nextUp.length > 0 && (
            <div className="border-t border-slate-100">
              <div className="px-3 pt-2.5 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-slate-400">Next up</div>
              <div className="pb-1">
                {nextUp.map((it) => <ItemRow key={it.id} it={it} program={itemKind(it) === 'medicare' ? PROGRAMS[0] : PROGRAMS[1]} showDate />)}
              </div>
            </div>
          )}
        </>
      )}

      {liveSelected && (
        <DetailPanel
          item={liveSelected}
          onClose={() => setSelectedItem(null)}
          onOpenInEmbeddedView={onOpenInEmbeddedView}
          formatDateDisplay={formatDateDisplay}
          effectiveStatus={getEffectiveStatus(liveSelected)}
          statusStyle={statusStyle}
          effectiveStatusLabel={effectiveStatusLabel}
          calendarStatusLabel={calendarStatusLabel}
          isCalendarNonActionable={isCalendarNonActionable}
          isCompliance={isCompliance}
          renderObligationAccountable={renderObligationAccountable}
          handleStatusChange={handleStatusChange}
          handleActionableChange={handleActionableChange}
          handleFetchGuidance={handleFetchGuidance}
          statusUpdatingId={statusUpdatingId}
          actionableUpdatingId={actionableUpdatingId}
          guidanceByObligationId={guidanceByObligationId}
          isCalendarObligationCompleted={isCalendarObligationCompleted}
        />
      )}
    </section>
  );
}
